import type { Capacidad } from '../../domain/policies/PoliticaAcceso';
import type { NombreIcono } from '../components/ui/iconos';

export interface EntradaNavegacion {
  /** Ruta relativa dentro de la aplicación. */
  ruta: string;
  etiqueta: string;
  icono: NombreIcono;
  /** Capacidad exigida para ver la entrada y entrar en la ruta. */
  capacidad: Capacidad;
  /** Texto de apoyo que se muestra al pasar el cursor. */
  descripcion: string;
}

export interface GrupoNavegacion {
  titulo: string;
  entradas: readonly EntradaNavegacion[];
}

/**
 * Estructura del menú.
 *
 * Es la única fuente de verdad: de aquí salen tanto los enlaces de la barra
 * lateral como la comprobación de permiso de cada ruta. En la versión anterior
 * eran dos listas separadas, y por eso varias secciones quedaban accesibles
 * escribiendo la dirección a mano aunque el menú las ocultara.
 *
 * ---------------------------------------------------------------------------
 * CÓMO SE AGRUPA: por aquello de lo que se habla, no por lo que se hace.
 *
 * Antes los grupos eran «Equipos», «Configuración» y «Notificaciones», es
 * decir, por tipo de tarea. El resultado era que las cosas de un mismo asunto
 * quedaban repartidas por todo el menú: los sensores estaban en «Equipos», sus
 * tipos también, y sus umbrales dos grupos más abajo. Para tocar algo de
 * sensores había que recorrer la barra entera.
 *
 * Ahora cada grupo es un dominio completo: dentro de «Sensores» está todo lo de
 * sensores, dentro de «Energía» todo lo de medidores. Se busca por tema, que es
 * como uno lo tiene en la cabeza.
 *
 * Los grupos son más y más cortos a propósito: un grupo de once entradas no
 * agrupa nada. Y como el armazón esconde los grupos sin entradas visibles,
 * cada rol ve sólo los suyos: un visualizador ve tres o cuatro títulos, no
 * nueve con un enlace cada uno.
 *
 * Ubicaciones no tiene grupo propio a propósito. Lo que eran tres pantallas
 * —«Personal», «Rutas» y «En ruta ahora»— vive dentro del propio mapa, en las
 * pestañas de su panel lateral: los puntos se colocan pulsando donde van, las
 * rutas se encomiendan viendo por dónde anda la gente, y el estado de cada
 * recorrido está junto a la persona que lo hace. Sacar todo eso a tablas
 * aparte obligaba a salir del mapa para cualquier cosa y volver a él para
 * comprobarla.
 *
 * `Sistema` es la excepción a la regla del dominio: son ajustes de la
 * plataforma —catálogos globales y conexiones con terceros— que sólo ve el
 * superadministrador. Repartirlos por dominio los habría dejado mezclados con
 * lo que usa todo el mundo a diario.
 * ---------------------------------------------------------------------------
 */
export const NAVEGACION: readonly GrupoNavegacion[] = [
  {
    /*
     * Todo lo que se mira en vivo, junto: valores, alarmas, mando, cámaras y
     * mapa. Es la pantalla de guardia, y quien está de guardia salta entre
     * estas siete sin tocar nada de configuración.
     */
    titulo: 'En vivo',
    entradas: [
      {
        ruta: '/',
        etiqueta: 'Panel',
        icono: 'dashboard',
        capacidad: 'ver_dashboard',
        descripcion: 'Vista general de la instalación',
      },
      {
        ruta: '/alarmas',
        etiqueta: 'Alarmas',
        icono: 'alarmas',
        capacidad: 'ver_alarmas',
        descripcion: 'Alarmas activas e historial',
      },
      {
        ruta: '/monitoreo',
        etiqueta: 'Monitoreo',
        icono: 'monitoreo',
        capacidad: 'ver_monitoreo',
        descripcion: 'Valores de sensores en tiempo real',
      },
      {
        ruta: '/control',
        etiqueta: 'Control',
        icono: 'control',
        capacidad: 'ver_control',
        descripcion: 'Encender y apagar equipos',
      },
      {
        ruta: '/energia',
        etiqueta: 'Consumo',
        icono: 'energia',
        capacidad: 'ver_energia',
        descripcion: 'Consumo y medidores eléctricos',
      },
      {
        ruta: '/camaras',
        etiqueta: 'Cámaras',
        icono: 'camara',
        capacidad: 'ver_camaras',
        descripcion: 'Videovigilancia',
      },
      {
        ruta: '/ubicaciones',
        etiqueta: 'Ubicaciones',
        icono: 'mapa',
        capacidad: 'ver_gps',
        descripcion: 'Mapa de personal en campo',
      },
      {
        ruta: '/alertas-ubicacion',
        etiqueta: 'Alertas de ubicación',
        icono: 'cerca',
        capacidad: 'ver_gps',
        descripcion: 'Desvíos del camino, aproximaciones y llegadas',
      },
    ],
  },
  {
    // Dónde está todo: la jerarquía cliente → área → nodo.
    titulo: 'Instalación',
    entradas: [
      {
        ruta: '/clientes',
        etiqueta: 'Clientes',
        icono: 'clientes',
        capacidad: 'ver_clientes',
        descripcion: 'Empresas atendidas',
      },
      {
        ruta: '/areas',
        etiqueta: 'Áreas',
        icono: 'areas',
        capacidad: 'ver_areas',
        descripcion: 'Zonas de cada cliente',
      },
      {
        ruta: '/nodos',
        etiqueta: 'Nodos',
        icono: 'nodos',
        capacidad: 'ver_nodos',
        descripcion: 'Equipos instalados en campo',
      },
    ],
  },
  {
    titulo: 'Sensores',
    entradas: [
      {
        ruta: '/sensores',
        etiqueta: 'Sensores',
        icono: 'sensores',
        capacidad: 'ver_sensores',
        descripcion: 'Puntos de medición',
      },
      {
        ruta: '/umbrales-analogicos',
        etiqueta: 'Umbrales analógicos',
        icono: 'umbrales',
        capacidad: 'ver_umbrales',
        descripcion: 'Rangos que disparan alarmas',
      },
      {
        ruta: '/umbrales-digitales',
        etiqueta: 'Umbrales digitales',
        icono: 'umbrales',
        capacidad: 'ver_umbrales',
        descripcion: 'Estados esperados y horarios',
      },
    ],
  },
  {
    titulo: 'Actuadores',
    entradas: [
      {
        ruta: '/actuadores',
        etiqueta: 'Actuadores',
        icono: 'actuadores',
        capacidad: 'ver_actuadores',
        descripcion: 'Elementos controlables',
      },
      {
        ruta: '/automatizacion',
        etiqueta: 'Automatización',
        icono: 'horario',
        capacidad: 'ver_automatizacion',
        descripcion: 'Reglas y horarios de actuadores',
      },
    ],
  },
  {
    // El consumo en vivo está arriba; aquí queda su configuración.
    titulo: 'Energía',
    entradas: [
      {
        ruta: '/medidores',
        etiqueta: 'Medidores',
        icono: 'medidor',
        capacidad: 'ver_medidores',
        descripcion: 'Medidores de energía',
      },
      {
        ruta: '/cuentas-ekm',
        etiqueta: 'Cuentas de medición',
        icono: 'llave',
        capacidad: 'ver_cuentas_ekm',
        descripcion: 'Credenciales de los medidores',
      },
      {
        ruta: '/umbrales-energia',
        etiqueta: 'Umbrales de energía',
        icono: 'umbrales',
        capacidad: 'ver_umbrales',
        descripcion: 'Límites de los medidores',
      },
      {
        ruta: '/alarmas-energia',
        etiqueta: 'Alarmas de energía',
        icono: 'alarmas',
        capacidad: 'ver_umbrales',
        descripcion: 'Alarmas generadas por medidores',
      },
    ],
  },
  {
    // Las cámaras en vivo están arriba; aquí queda su configuración.
    titulo: 'Cámaras',
    entradas: [
      {
        ruta: '/dvr',
        etiqueta: 'Grabadores',
        icono: 'grabador',
        capacidad: 'ver_grabadores',
        descripcion: 'Grabadores de vídeo y su enlace',
      },
      {
        ruta: '/vistas',
        etiqueta: 'Vistas',
        icono: 'vistaRejilla',
        capacidad: 'ver_vistas',
        descripcion: 'Cámaras de cada grabador',
      },
    ],
  },
  {
    // A quién se avisa, cómo se escala y qué salió.
    titulo: 'Avisos',
    entradas: [
      {
        ruta: '/contactos',
        etiqueta: 'Contactos',
        icono: 'contactos',
        capacidad: 'ver_contactos',
        descripcion: 'A quién se avisa y por qué medio',
      },
      {
        ruta: '/escalamiento',
        etiqueta: 'Escalamiento',
        icono: 'escalamiento',
        capacidad: 'ver_escalamiento',
        descripcion: 'Niveles de aviso por severidad',
      },
      {
        ruta: '/envios',
        etiqueta: 'Envíos',
        icono: 'outbox',
        capacidad: 'ver_outbox',
        descripcion: 'Registro de notificaciones enviadas',
      },
      {
        ruta: '/bitacora',
        etiqueta: 'Sin entregar',
        icono: 'historial',
        capacidad: 'ver_bitacora',
        descripcion: 'Avisos que no pudieron entregarse',
      },
    ],
  },
  {
    titulo: 'Informes',
    entradas: [
      {
        ruta: '/reportes',
        etiqueta: 'Programados',
        icono: 'reporte',
        capacidad: 'ver_config_reportes',
        descripcion: 'Informes mensuales automáticos',
      },
      {
        ruta: '/reportes-generados',
        etiqueta: 'Generados',
        icono: 'descargar',
        capacidad: 'ver_reportes_generados',
        descripcion: 'Historial de informes en PDF',
      },
    ],
  },
  {
    // Ajustes de la plataforma, no de un cliente. Sólo superadministrador.
    titulo: 'Sistema',
    entradas: [
      {
        ruta: '/usuarios',
        etiqueta: 'Usuarios',
        icono: 'usuarios',
        capacidad: 'ver_usuarios',
        descripcion: 'Altas, roles y permisos',
      },
      {
        ruta: '/tipos-sensores',
        etiqueta: 'Tipos de sensores',
        icono: 'categoria',
        capacidad: 'ver_tipos_sensores',
        descripcion: 'Catálogo de tipos disponibles',
      },
      {
        ruta: '/tipos-actuadores',
        etiqueta: 'Tipos de actuadores',
        icono: 'categoria',
        capacidad: 'ver_tipos_actuadores',
        descripcion: 'Tipos permitidos por cliente',
      },
      {
        ruta: '/integraciones',
        etiqueta: 'Integraciones',
        icono: 'webhook',
        capacidad: 'ver_webhooks',
        descripcion: 'Envío a sistemas externos',
      },
    ],
  },
];

/** Todas las entradas en una sola lista, para resolver rutas. */
export const ENTRADAS: readonly EntradaNavegacion[] = NAVEGACION.flatMap((g) => g.entradas);

export function buscarEntrada(ruta: string): EntradaNavegacion | undefined {
  return ENTRADAS.find((e) => e.ruta === ruta);
}
